import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ResponsiveContainer, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';

interface BarChartData {
  [key: string]: string | number;
}

interface BarChartProps extends ChartHeaderProps {
  data: BarChartData[];
  dataKeys: string[];
  xAxisKey: string; // used as the category key (y-axis) for horizontal layout to keep parity with BarChart
  colors?: string[];
  height?: number;
  stacked?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
}

const HorizontalBarChart: React.FC<BarChartProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 300,
  stacked = false,
  xAxisTitle,
  yAxisTitle,
  details
}) => {
  return (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ padding: 1 }}>
        <ChartHeader title={title} description={description} details={details} />
        <ResponsiveContainer width="100%" height={height}>
          <RechartsBarChart
            layout="vertical"
            data={data}
            margin={{ top: 20, right: 30, left: 5, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -2, style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
            />
            <YAxis
              dataKey={xAxisKey}
              type="category"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              width={120}
              label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: '12px'
              }}
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            />
            {dataKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                stackId={stacked ? 'a' : undefined}
                radius={stacked ? 0 : [0, 4, 4, 0]}
                maxBarSize={35}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
        {details?.bottomDescription && (
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center', marginTop: 1 }}>
            {details.bottomDescription || ''}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default HorizontalBarChart;
